import { z } from 'zod';

/**
 * 인생 타임라인 — 이 서비스의 본체.
 *
 * 기존 Life Timeline은 계정이 없었다. 브라우저에 `editId`(UUID)를 들고 있는
 * 사람이 곧 주인이었고, 데이터는 S3의 json 한 덩어리였다. 여기서는 계정에
 * 붙는다 — 주소(`handle`)로 열리고, 로그인한 사람에게 열린다(잠그면 팔로워만).
 *
 * **주제(Topic)와 사건(Event)으로 이루어진다.** 사건은 주제에 속하고, 주제는
 * 색을 갖는다. 화면은 연도 내림차순 세로축에 사건을 찍는다.
 */

/**
 * 주제 색으로 **처음 제안하는** 값들. 고를 수 있는 전부가 아니다.
 *
 * 색은 자유롭게 적을 수 있다(`#` + 여섯 자리). 여덟 개로 묶어 두면 아홉 번째
 * 주제부터는 남의 색을 써야 하고, 원래 Life Timeline도 색을 직접 넣는
 * 방식이었다. 여기 있는 값은 **새로 만들 때 무작위로 하나 골라 채워 주는**
 * 용도다 — 빈 색 칸 앞에서 멈추지 않게.
 */
export const TOPIC_COLOR_SUGGESTIONS = [
  '#58a6ff',
  '#f97316',
  '#a78bfa',
  '#e11d48',
  '#10b981',
  '#eab308',
  '#ec4899',
  '#14b8a6',
] as const;

/** `#rrggbb`. 세 자리 축약(`#fff`)은 받지 않는다 — 저장 형태가 둘이 되면 비교가 흔들린다 */
export const hexColorSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^#[0-9a-f]{6}$/, 'color must be #rrggbb');

/** 새 주제를 만들 때 채워 줄 색 하나 */
export function randomTopicColor(): string {
  const index = Math.floor(Math.random() * TOPIC_COLOR_SUGGESTIONS.length);
  return TOPIC_COLOR_SUGGESTIONS[index] ?? TOPIC_COLOR_SUGGESTIONS[0];
}

/**
 * **무료로** 가질 수 있는 최대치.
 *
 * 넘으면 막는 것이 아니라 **결제 화면으로 보낸다** — 여기까지 쓴 사람은 이미
 * 이 서비스를 쓰고 있는 사람이라, 벽을 세우는 것보다 길을 내는 편이 맞다.
 *
 * 상한이 있어도 **읽는 쪽은 상한에 기대지 않는다**(`TIMELINE_PAGE_SIZE`).
 * 상한을 푸는 순간 설계가 무너지면 그건 설계가 아니다.
 */
export const MAX_TOPICS = 20;
export const MAX_EVENTS = 300;

/**
 * **한 해에** 담을 수 있는 수.
 *
 * ⚠️ 이건 장사 결정이 아니라 **DynamoDB의 벽**이다. 기록을 맨 위에 넣으면
 * 그 해 형제들의 자리를 한 트랜잭션으로 밀어 내리는데, `TransactWriteItems`는
 * 100개가 끝이다. 전체 상한이 30일 때는 닿을 수 없는 길이라 안 두고 있었다.
 *
 * 100이 아니라 90인 이유는 **미는 줄 말고도 새로 넣는 줄이 있어서**다.
 * 꽉 채워 두면 경계에서 개발자용 오류가 그대로 사람에게 간다.
 *
 * 이걸 더 올리려면 자리 바꾸기를 트랜잭션이 아닌 방식으로 다시 짜야 한다.
 */
export const MAX_EVENTS_PER_YEAR = 90;

/**
 * 타임라인을 한 번에 몇 개씩 내려줄지.
 *
 * 상한(`MAX_EVENTS`)보다 **넉넉히 작게** 둔다 — 상한 안에서도 쪽이 나뉘어야
 * 페이징이 **실제로 도는 길**이 된다. 상한만 믿고 큰 값을 두면, 상한을 푸는
 * 날 처음 도는 코드가 된다.
 *
 * ⚠️ 상한을 30으로 내리면서 이것도 같이 내렸다. 둘이 같으면 쪽이 한 번도
 * 안 나뉘어, 이어 읽기 코드가 배포된 적 없는 것과 같아진다.
 */
export const TIMELINE_PAGE_SIZE = 10;

export const topicIdSchema = z.string().regex(/^tp_[0-9a-z]{10}$/, 'invalid topic id');
export const eventIdSchema = z.string().regex(/^ev_[0-9a-z]{10}$/, 'invalid event id');

export const topicInputSchema = z.object({
  label: z.string().trim().min(1).max(30),
  color: hexColorSchema,
});
export type TopicInput = z.infer<typeof topicInputSchema>;

/**
 * 사건.
 *
 * `year`만 받고 월·일은 받지 않는다. 인생을 훑는 축이라 연 단위면 충분하고,
 * 날짜까지 받으면 기억나지 않는 값을 지어내게 된다. 같은 해 안의 순서는
 * `position`이 정한다 — 사용자가 끌어서 정한 자리다.
 */
export const yearSchema = z.number().int().min(1900).max(2200);

/** 1~12. 연도 안의 달 */
export const monthSchema = z.number().int().min(1).max(12);

/**
 * 기록에 거는 링크.
 *
 * `https`만 받는다 — `http`는 누르면 경고가 뜨고, `javascript:` 같은 것은
 * 남의 프로필에서 실행되면 그대로 사고다. 길이를 300으로 끊는 이유는 화면이
 * 아니라 **저장**이다. 화면에는 어차피 도메인만 보여준다.
 */
export const eventUrlSchema = z
  .string()
  .trim()
  .max(300)
  .url()
  .refine((value) => value.startsWith('https://'), { message: 'url must be https' });

const eventFields = z.object({
  topicId: topicIdSchema,
  year: yearSchema,
  title: z.string().trim().min(1).max(80),
  note: z.string().trim().max(200).optional(),
  /**
   * 기간의 시작 **달**. 연도는 `year`가 갖는다.
   *
   * 달을 안 적으면 기간이 아예 없는 것으로 본다 — 인생을 훑는 축이라
   * "2019년"만으로 충분한 기록이 많고, 달을 강제하면 기억나지 않는 값을
   * 지어내게 된다.
   */
  startMonth: monthSchema.optional(),
  /** 끝난 연·월. **둘 다 있거나 둘 다 없다.** 없으면 "현재"로 읽는다 */
  endYear: yearSchema.optional(),
  endMonth: monthSchema.optional(),
  url: eventUrlSchema.optional(),
});

/**
 * 기간의 앞뒤가 말이 되는지.
 *
 * 스키마 객체가 아니라 **함수로** 빼 둔 이유: 저장 요청은 여기에 `eventId`를
 * 더한 모양인데, `superRefine`을 건 스키마에는 `.extend`가 없다. 검사를
 * 함수로 두면 두 스키마가 같은 규칙을 나눠 쓴다.
 */
const checkPeriod = (
  value: {
    year: number;
    startMonth?: number | undefined;
    endYear?: number | undefined;
    endMonth?: number | undefined;
  },
  ctx: z.RefinementCtx,
): void => {
  const hasEnd = value.endYear !== undefined || value.endMonth !== undefined;
  if (hasEnd && (value.endYear === undefined || value.endMonth === undefined)) {
    ctx.addIssue({ code: 'custom', message: '끝은 연도와 달을 같이 적어야 합니다' });
    return;
  }
  if (hasEnd && value.startMonth === undefined) {
    // 시작을 모르는데 끝만 있으면 화면에 "? ~ 2020.02"가 된다
    ctx.addIssue({ code: 'custom', message: '끝을 적으려면 시작 달도 적어야 합니다' });
    return;
  }
  if (
    value.endYear !== undefined &&
    value.endMonth !== undefined &&
    value.startMonth !== undefined &&
    value.endYear * 12 + value.endMonth < value.year * 12 + value.startMonth
  ) {
    ctx.addIssue({ code: 'custom', message: '끝이 시작보다 앞설 수 없습니다' });
  }
};

export const eventInputSchema = eventFields.superRefine(checkPeriod);
export type EventInput = z.infer<typeof eventInputSchema>;

export interface EventPeriod {
  /** `2020.01` */
  start: string;
  /** `2020.02`. 끝을 안 적었으면 `null`(= 지금까지) */
  end: string | null;
  /** 시작부터 끝(또는 오늘)까지 몇 달인가. 같은 달이면 0 */
  months: number;
}

const yearMonth = (year: number, month: number): string =>
  `${year}.${String(month).padStart(2, '0')}`;

/**
 * 기록의 기간을 **글로 쓸 재료**로 바꾼다.
 *
 * 말로 만드는 일(`1개월`·`현재`)은 화면이 한다 — 여기는 언어를 모르는
 * 자리라, 붙여 쓰면 영어 화면에서 한국어가 튀어나온다.
 *
 * 시작 달이 없으면 `null`이다. 그런 기록은 지금처럼 연도만 찍힌다.
 */
export function eventPeriod(
  event: { year: number; startMonth?: number; endYear?: number; endMonth?: number },
  now: Date = new Date(),
): EventPeriod | null {
  if (event.startMonth === undefined) {
    return null;
  }
  const startIndex = event.year * 12 + event.startMonth;
  const ended = event.endYear !== undefined && event.endMonth !== undefined;
  const endIndex = ended
    ? event.endYear! * 12 + event.endMonth!
    : now.getFullYear() * 12 + (now.getMonth() + 1);

  return {
    start: yearMonth(event.year, event.startMonth),
    end: ended ? yearMonth(event.endYear!, event.endMonth!) : null,
    // 아직 안 끝난 기록이 오늘보다 미래에서 시작했으면 0으로 둔다
    months: Math.max(0, endIndex - startIndex),
  };
}

/**
 * 주제 차례 바꾸기. **전체 목록을 통째로** 받는다.
 *
 * 옮긴 것만 보내면 서버가 나머지 자리를 추측해야 하고, 그 추측이 화면과
 * 어긋나면 사용자는 방금 옮긴 것이 제자리로 돌아간 것처럼 본다.
 * 통째로 보내면 같은 요청을 두 번 보내도 결과가 같다(픽과 같은 규칙).
 */
export const reorderTopicsRequestSchema = z.object({
  topicIds: z.array(z.string().min(1).max(40)).min(1).max(MAX_TOPICS),
});
export type ReorderTopicsRequest = z.infer<typeof reorderTopicsRequestSchema>;

export interface TopicResponse {
  topicId: string;
  label: string;
  color: string;
}

export interface EventResponse {
  eventId: string;
  topicId: string;
  year: number;
  title: string;
  note?: string;
  startMonth?: number;
  endYear?: number;
  endMonth?: number;
  url?: string;
  /** 같은 해 안에서의 자리. 작을수록 위 */
  position: number;
}

export interface TimelineResponse {
  /** 주제는 **전부** 온다 — 상한이 작고(≤10) 화면의 축이다 */
  topics: TopicResponse[];
  /** 기록은 **한 쪽씩** */
  events: EventResponse[];
  /**
   * 이어 읽을 자리. 없으면 끝이다.
   *
   * 몇 번째부터가 아니라 **어디서부터**다 — 보는 동안 앞에서 기록이 생기거나
   * 사라져도 건너뛰거나 두 번 보이지 않는다.
   */
  nextCursor: string | null;
  /**
   * 기록 **전부**의 개수. `events`는 한 쪽만 와 있다.
   *
   * ⚠️ 이게 없을 때 화면이 "커서가 남아 있으면 상한에 닿은 것"으로 쳤다가,
   * **22개 가진 사람이 기록을 못 넣었다** — 첫 쪽(10개) 뒤에 커서가 남으니
   * 늘 상한으로 읽혔다. 세는 일은 서버가 한다(`Select: COUNT`라 줄을 실어
   * 오지 않는다).
   *
   * ⚠️ **첫 쪽에만 온다**(이어지는 쪽은 `null`). 세는 값은 총량에 비례해
   * 커지는데, 쪽마다 세면 스크롤해서 서른 쪽을 넘긴 사람은 서른 번 센다.
   * 상한에 닿았는지는 처음 불러올 때 정해지므로 한 번이면 된다. 받는 쪽은
   * **처음 받은 값을 들고 간다.**
   */
  eventTotal: number | null;
}

/** 주제 저장. `topicId`가 없으면 새로 만든다 */
export const saveTopicRequestSchema = topicInputSchema.extend({
  topicId: topicIdSchema.optional(),
});
export type SaveTopicRequest = z.infer<typeof saveTopicRequestSchema>;

/** 사건 저장. `eventId`가 없으면 새로 만든다 */
export const saveEventRequestSchema = eventFields
  .extend({ eventId: eventIdSchema.optional() })
  .superRefine(checkPeriod);
export type SaveEventRequest = z.infer<typeof saveEventRequestSchema>;

/**
 * 순서 바꾸기.
 *
 * **건드린 해마다 그 해의 전부를 보낸다.** 부분만 보내면 빠진 사건이 자리를
 * 잃는다. 해를 넘겨 끌면 두 해가 한꺼번에 바뀌므로(나간 해와 들어온 해)
 * 목록이 여럿이다 — 따로 두 번 보내면 그 사이에 한쪽만 적용된 상태가 보인다.
 *
 * 비어 있는 `eventIds`도 받는다 — 마지막 하나가 다른 해로 떠나면 그 해는
 * 빈 목록이 된다.
 */
export const reorderEventsRequestSchema = z.object({
  years: z
    .array(
      z.object({
        year: yearSchema,
        /* 한 해의 전부를 보내는 자리다 — 전체 상한이 아니라 **한 해 상한**이 맞다 */
        eventIds: z.array(eventIdSchema).max(MAX_EVENTS_PER_YEAR),
      }),
    )
    .min(1)
    .max(8),
});
export type ReorderEventsRequest = z.infer<typeof reorderEventsRequestSchema>;

/**
 * 화면에 그릴 순서로 세운다.
 *
 * 연도 **내림차순**(최근이 위), 같은 해 안에서는 `position` 오름차순.
 * 기존 소스도 최근을 위에 뒀다 — 인생 타임라인을 열면 지금이 먼저 보여야 한다.
 *
 * 순서의 주인은 사용자다 — 같은 해 안의 차례는 끌어서 정한다.
 */
export interface SortableEvent {
  eventId: string;
  year: number;
  position: number;
}

export function sortEvents<T extends SortableEvent>(events: readonly T[]): T[] {
  return [...events].sort(
    (a, b) => b.year - a.year || a.position - b.position || a.eventId.localeCompare(b.eventId),
  );
}
